import { useState, useRef, useMemo } from 'react';

import { Customer, useCustomersStore, useLoansStore } from '@/hooks/useSupabaseStore';
import { fetchCustomerImages, saveCustomerImages, deleteCustomerImages } from '@/lib/customerImages';

interface CustomerWithCccd extends Customer {
  cccdFront?: string;
  cccdBack?: string;
}

const emptyForm: Omit<CustomerWithCccd, 'id'> = {
  name: '', phone: '', idCard: '', address: '',
  createdAt: new Date().toISOString().slice(0, 10),
  totalLoans: 0, activeLoans: 0, totalBorrowed: 0,
  cccdFront: '', cccdBack: '',
};

export default function CustomersPage() {
  const { customers, addCustomer, updateCustomer, deleteCustomer } = useCustomersStore();
  const { loans } = useLoansStore();

  // Tính động số liệu khoản vay từng khách hàng từ danh sách loans
  const customerStats = useMemo(() => {
    const stats: Record<string, { totalLoans: number; activeLoans: number; totalBorrowed: number }> = {};
    loans.forEach(l => {
      if (!stats[l.customerId]) {
        stats[l.customerId] = { totalLoans: 0, activeLoans: 0, totalBorrowed: 0 };
      }
      stats[l.customerId].totalLoans += 1;
      if (l.status !== 'redeemed' && l.status !== 'forfeited') {
        stats[l.customerId].activeLoans += 1;
        stats[l.customerId].totalBorrowed += l.loanAmount;
      }
    });
    return stats;
  }, [loans]);

  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<'none' | 'totalLoans' | 'activeLoans' | 'totalBorrowed' | 'createdAt'>('none');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [sortOpen, setSortOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<Omit<CustomerWithCccd, 'id'>>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [viewCustomer, setViewCustomer] = useState<CustomerWithCccd | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<CustomerWithCccd | null>(null);
  const [toast, setToast] = useState('');

  const cccdFrontRef = useRef<HTMLInputElement>(null);
  const cccdBackRef = useRef<HTMLInputElement>(null);
  const editFrontRef = useRef<HTMLInputElement>(null);
  const editBackRef = useRef<HTMLInputElement>(null);

  const getCccdImages = (id: string) => fetchCustomerImages(id);
  const saveCccdImages = (id: string, front?: string, back?: string) => saveCustomerImages(id, front, back);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) ||
    c.idCard.includes(search)
  );

  const filteredAndSorted = [...filtered].sort((a, b) => {
    if (sortField === 'none') return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const sa = customerStats[a.id] ?? { totalLoans: 0, activeLoans: 0, totalBorrowed: 0 };
    const sb = customerStats[b.id] ?? { totalLoans: 0, activeLoans: 0, totalBorrowed: 0 };
    if (sortField === 'totalLoans') return (sa.totalLoans - sb.totalLoans) * dir;
    if (sortField === 'activeLoans') return (sa.activeLoans - sb.activeLoans) * dir;
    if (sortField === 'totalBorrowed') return (sa.totalBorrowed - sb.totalBorrowed) * dir;
    if (sortField === 'createdAt') return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
    return 0;
  });

  const toggleSort = (field: typeof sortField) => {
    if (field === 'none') { setSortField('none'); return; }
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortOptions: { key: typeof sortField; label: string }[] = [
    { key: 'createdAt', label: 'Ngày đăng ký' },
    { key: 'totalLoans', label: 'Tổng vay' },
    { key: 'activeLoans', label: 'Đang vay' },
    { key: 'totalBorrowed', label: 'Tổng dư nợ' },
  ];

  const currentSortLabel = sortField === 'none' ? 'Sắp xếp' : sortOptions.find(o => o.key === sortField)?.label;



  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: 'cccdFront' | 'cccdBack',
    target: 'add' | 'edit'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const val = ev.target?.result as string;
      if (target === 'add') setForm(prev => ({ ...prev, [key]: val }));
      else setEditForm(prev => prev ? { ...prev, [key]: val } : prev);
    };
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!form.name || !form.phone || !form.idCard) {
      showToast('Vui lòng điền đầy đủ thông tin bắt buộc!');
      return;
    }
    const newId = `KH${Date.now()}`;
    try {
      await addCustomer({ ...form, id: newId });
      if (form.cccdFront || form.cccdBack) {
        await saveCccdImages(newId, form.cccdFront, form.cccdBack);
      }
      setShowAdd(false);
      setForm(emptyForm);
      showToast('Thêm khách hàng thành công!');
    } catch {
      showToast('Lỗi: Không thể thêm khách hàng. Vui lòng thử lại!');
    }
  };

  const handleDelete = async (id: string) => {
    await deleteCustomer(id);
    await deleteCustomerImages(id);
    setDeleteConfirm(null);
    setViewCustomer(null);
    showToast('Đã xóa khách hàng! Khoản vay, tài sản và doanh thu liên kết đã được dọn sạch.');
  };

  const openView = async (c: Customer) => {
    const imgs = await getCccdImages(c.id);
    const cWithImg = { ...c, cccdFront: imgs.front, cccdBack: imgs.back };
    setViewCustomer(cWithImg);
    setEditMode(false);
    setEditForm(null);
  };

  const startEdit = () => {
    if (!viewCustomer) return;
    setEditForm({ ...viewCustomer });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm) return;
    if (!editForm.name || !editForm.phone || !editForm.idCard) {
      showToast('Vui lòng điền đầy đủ thông tin bắt buộc!');
      return;
    }
    await updateCustomer(editForm.id, {
      name: editForm.name,
      phone: editForm.phone,
      idCard: editForm.idCard,
      address: editForm.address,
    });
    await saveCccdImages(editForm.id, editForm.cccdFront, editForm.cccdBack);
    setViewCustomer(editForm);
    setEditMode(false);
    showToast('Cập nhật thông tin thành công!');
  };

  // Upload ảnh component tái sử dụng
  const CccdUpload = ({
    label, value, onClear, inputRef, onChange,
  }: {
    label: string;
    value?: string;
    onClear: () => void;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <div>
      <label className="text-xs text-gray-500 font-medium block mb-2">{label}</label>
      <div
        className="relative border-2 border-dashed border-gray-200 rounded-xl overflow-hidden bg-gray-50 cursor-pointer hover:border-amber-400 transition-colors"
        style={{ height: 110 }}
        onClick={() => inputRef.current?.click()}
      >
        {value ? (
          <>
            <img src={value} alt={label} className="w-full h-full object-cover" />
            <button
              className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full text-xs hover:bg-red-600 cursor-pointer"
              onClick={e => { e.stopPropagation(); onClear(); }}
            >
              <i className="ri-close-line"></i>
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-1.5 text-gray-400">
            <span className="w-8 h-8 flex items-center justify-center"><i className="ri-camera-line text-2xl"></i></span>
            <span className="text-xs">Nhấn để tải ảnh</span>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onChange} />
    </div>
  );

  return (<>
      {toast && (
        <div className="fixed top-5 right-5 z-[100] bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium">{toast}</div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 md:mb-6">
        {[
          { label: 'Tổng khách hàng', value: customers.length, color: 'text-gray-800' },
          { label: 'Đang vay', value: customers.filter(c => (customerStats[c.id]?.activeLoans ?? 0) > 0).length, color: 'text-blue-600' },
          { label: 'Khách hàng mới (T4)', value: customers.filter(c => c.createdAt.startsWith('2026-04')).length, color: 'text-emerald-600' },
          { label: 'Tổng dư nợ', value: `${(customers.reduce((s, c) => s + (customerStats[c.id]?.totalBorrowed ?? 0), 0) / 1000000).toFixed(0)}M đ`, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-3 md:p-4">
            <p className="text-gray-400 text-xs mb-1">{s.label}</p>
            <p className={`font-bold text-xl md:text-2xl ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-sm md:text-base">Danh sách khách hàng</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400"><i className="ri-search-line text-sm"></i></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm kiếm..." className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] w-36 md:w-44" />
            </div>
            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setSortOpen(o => !o)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap ${sortField !== 'none' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-up-down-line text-sm"></i></span>
                <span className="hidden sm:inline">{currentSortLabel}</span>
                <span className="w-3 h-3 flex items-center justify-center"><i className={`ri-arrow-down-s-line text-xs transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i></span>
              </button>
              {sortOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
                  <div className="absolute right-0 top-full mt-1.5 w-56 bg-white border border-gray-100 rounded-xl overflow-hidden z-50">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Sắp xếp theo</div>
                    {sortOptions.map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => {
                          if (sortField === opt.key) {
                            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortField(opt.key);
                            setSortDir('desc');
                          }
                          setSortOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between transition-colors cursor-pointer ${sortField === opt.key ? 'bg-amber-50 text-amber-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <span>{opt.label}</span>
                        {sortField === opt.key && (
                          <span className="w-4 h-4 flex items-center justify-center text-amber-600">
                            <i className={sortDir === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                          </span>
                        )}
                      </button>
                    ))}
                    {sortField !== 'none' && (
                      <>
                        <div className="border-t border-gray-100" />
                        <button
                          onClick={() => { setSortField('none'); setSortDir('desc'); setSortOpen(false); }}
                          className="w-full text-left px-3 py-2.5 text-sm text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2"
                        >
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-xs"></i></span>
                          Bỏ sắp xếp
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => { setShowAdd(true); setForm(emptyForm); }} className="flex items-center gap-1.5 px-3 py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base"></i></span>
              <span className="hidden sm:inline">Thêm mới</span>
            </button>
          </div>
        </div>

        {/* Mobile: Card list */}
        <div className="block md:hidden divide-y divide-gray-50">
          {filteredAndSorted.map(c => (
            <div key={c.id} className="px-4 py-3.5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <span className="text-xs font-bold text-[#f59e0b]">{c.id}</span>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">{c.name}</p>
                  <p className="text-xs text-gray-400">{c.phone}</p>
                </div>
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap shrink-0 ${(customerStats[c.id]?.activeLoans ?? 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  {(customerStats[c.id]?.activeLoans ?? 0) > 0 ? `${customerStats[c.id]?.activeLoans ?? 0} khoản vay` : 'Không vay'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
                <div>
                  <p className="text-xs text-gray-400">CMND/CCCD</p>
                  <p className="text-xs font-medium text-gray-700">{c.idCard}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Tổng dư nợ</p>
                  <p className="text-xs font-semibold text-gray-800">{(customerStats[c.id]?.totalBorrowed ?? 0).toLocaleString('vi-VN')}đ</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Ngày đăng ký</p>
                  <p className="text-xs text-gray-600">{c.createdAt}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Tổng lần vay</p>
                  <p className="text-xs text-gray-600">{customerStats[c.id]?.totalLoans ?? 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openView(c)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer text-amber-600 text-xs font-medium">
                  <i className="ri-eye-line text-sm"></i> Xem hồ sơ
                </button>
                <button onClick={() => setDeleteConfirm(c.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-400">
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
          {filteredAndSorted.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-contacts-book-2-line text-4xl mb-2 block"></i>
              <p className="text-sm">Không tìm thấy khách hàng nào</p>
            </div>
          )}
        </div>

        {/* Desktop: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Mã KH</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Họ tên</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Số điện thoại</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CMND/CCCD</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Địa chỉ</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Ngày đăng ký</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Tổng vay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Đang vay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Tổng dư nợ</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredAndSorted.map(c => (
                <tr key={c.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-4 py-3.5 text-sm font-bold text-[#f59e0b]">{c.id}</td>
                  <td className="px-4 py-3.5 text-sm font-medium text-gray-800 whitespace-nowrap">{c.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{c.phone}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{c.idCard}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500 max-w-[180px] truncate">{c.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">{c.createdAt}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-800 font-medium text-center">{customerStats[c.id]?.totalLoans ?? 0}</td>
                  <td className="px-4 py-3.5 text-center">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${(customerStats[c.id]?.activeLoans ?? 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{customerStats[c.id]?.activeLoans ?? 0}</span>
                  </td>
                  <td className="px-4 py-3.5 text-sm font-semibold text-gray-800 whitespace-nowrap">{(customerStats[c.id]?.totalBorrowed ?? 0).toLocaleString('vi-VN')}đ</td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openView(c)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer text-amber-500" title="Xem chi tiết">
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      <button onClick={() => setDeleteConfirm(c.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-400" title="Xóa">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredAndSorted.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-contacts-book-2-line text-4xl mb-2 block"></i>
              <p className="text-sm">Không tìm thấy khách hàng nào</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== MODAL: XEM / CHỈNH SỬA KHÁCH HÀNG ===== */}
      {viewCustomer && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setViewCustomer(null); setEditMode(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <span className="text-amber-700 font-bold text-base">{viewCustomer.name.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">{editMode ? 'Chỉnh sửa thông tin' : viewCustomer.name}</h3>
                  <p className="text-xs text-gray-400">{viewCustomer.id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editMode && (
                  <button
                    onClick={startEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line text-sm"></i></span>
                    Chỉnh sửa
                  </button>
                )}
                <button onClick={() => { setViewCustomer(null); setEditMode(false); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-gray-500 text-lg"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* ── CHẾ ĐỘ XEM ── */}
              {!editMode && (
                <>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Thông tin cá nhân</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Họ tên', value: viewCustomer.name },
                        { label: 'Số điện thoại', value: viewCustomer.phone },
                        { label: 'CMND/CCCD', value: viewCustomer.idCard },
                        { label: 'Ngày đăng ký', value: viewCustomer.createdAt },
                        { label: 'Địa chỉ', value: viewCustomer.address || '—', span: 2 },
                      ].map(item => (
                        <div key={item.label} className={item.span === 2 ? 'col-span-2' : ''}>
                          <p className="text-gray-400 text-xs mb-0.5">{item.label}</p>
                          <p className="text-gray-800 text-sm font-medium">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 bg-amber-50 rounded-xl p-4">
                    <div className="text-center">
                      <p className="text-amber-600 font-bold text-xl">{customerStats[viewCustomer.id]?.totalLoans ?? 0}</p>
                      <p className="text-gray-500 text-xs mt-0.5">Tổng lần vay</p>
                    </div>
                    <div className="text-center border-x border-amber-200">
                      <p className="text-blue-600 font-bold text-xl">{customerStats[viewCustomer.id]?.activeLoans ?? 0}</p>
                      <p className="text-gray-500 text-xs mt-0.5">Đang vay</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-800 font-bold text-base">{((customerStats[viewCustomer.id]?.totalBorrowed ?? 0) / 1000000).toFixed(1)}M</p>
                      <p className="text-gray-500 text-xs mt-0.5">Tổng dư nợ</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-id-card-line text-gray-400"></i></span>
                      Hình ảnh CCCD
                    </p>
                    {(viewCustomer.cccdFront || viewCustomer.cccdBack) ? (
                      <div className="grid grid-cols-2 gap-3">
                        {viewCustomer.cccdFront && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1.5 font-medium">Mặt trước</p>
                            <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: 120 }}>
                              <img src={viewCustomer.cccdFront} alt="CCCD mặt trước" className="w-full h-full object-cover" />
                            </div>
                            <a
                              href={viewCustomer.cccdFront}
                              download={`CCCD_${viewCustomer.name}_mat_truoc.jpg`}
                              className="mt-1.5 flex items-center justify-center gap-1 w-full py-1.5 rounded-lg bg-gray-50 hover:bg-amber-50 text-gray-500 hover:text-amber-600 text-xs font-medium transition-colors cursor-pointer border border-gray-100 hover:border-amber-200"
                            >
                              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line text-sm"></i></span>
                              Tải về
                            </a>
                          </div>
                        )}
                        {viewCustomer.cccdBack && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1.5 font-medium">Mặt sau</p>
                            <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: 120 }}>
                              <img src={viewCustomer.cccdBack} alt="CCCD mặt sau" className="w-full h-full object-cover" />
                            </div>
                            <a
                              href={viewCustomer.cccdBack}
                              download={`CCCD_${viewCustomer.name}_mat_sau.jpg`}
                              className="mt-1.5 flex items-center justify-center gap-1 w-full py-1.5 rounded-lg bg-gray-50 hover:bg-amber-50 text-gray-500 hover:text-amber-600 text-xs font-medium transition-colors cursor-pointer border border-gray-100 hover:border-amber-200"
                            >
                              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line text-sm"></i></span>
                              Tải về
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-200 rounded-xl py-6 text-center text-gray-400">
                        <span className="w-8 h-8 flex items-center justify-center mx-auto mb-1"><i className="ri-id-card-line text-2xl"></i></span>
                        <p className="text-xs">Chưa có ảnh CCCD</p>
                        <button onClick={startEdit} className="mt-2 text-xs text-amber-500 hover:underline cursor-pointer">Nhấn Chỉnh sửa để thêm ảnh</button>
                      </div>
                    )}
                  </div>

                  {/* ── LỊCH SỬ KHOẢN VAY ── */}
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-list-3-line text-gray-400"></i></span>
                      Lịch sử khoản vay
                    </p>
                    {(() => {
                      const customerLoans = loans.filter(l => l.customerId === viewCustomer!.id).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
                      const activeLoans = customerLoans.filter(l => l.status !== 'redeemed' && l.status !== 'forfeited');
                      const pastLoans = customerLoans.filter(l => l.status === 'redeemed' || l.status === 'forfeited');
                      const statusConfigMap = {
                        active: { label: 'Đang vay', cls: 'bg-blue-100 text-blue-700' },
                        overdue: { label: 'Quá hạn', cls: 'bg-red-100 text-red-700' },
                        due_soon: { label: 'Sắp đến hạn', cls: 'bg-amber-100 text-amber-700' },
                        redeemed: { label: 'Đã chuộc', cls: 'bg-emerald-100 text-emerald-700' },
                        forfeited: { label: 'Đã thanh lý', cls: 'bg-gray-100 text-gray-600' },
                        interest_paid: { label: 'Đã đóng lãi', cls: 'bg-purple-100 text-purple-700' },
                      };
                      return (
                        <div className="space-y-3">
                          {activeLoans.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-blue-600 mb-2">Đang vay ({activeLoans.length})</p>
                              <div className="space-y-2">
                                {activeLoans.map(loan => {
                                  const today = new Date(); today.setHours(0,0,0,0);
                                  const due = new Date(loan.dueDate); due.setHours(0,0,0,0);
                                  const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000*60*60*24));
                                  const isOverdue = daysLeft < 0;
                                  const isDueSoon = daysLeft >= 0 && daysLeft <= 7;
                                  const cfg = statusConfigMap[loan.status];
                                  return (
                                    <div key={loan.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                                      <div className="flex items-start justify-between gap-2 mb-1.5">
                                        <div>
                                          <p className="text-sm font-bold text-gray-800">{loan.id}</p>
                                          <p className="text-xs text-gray-500">{loan.assetName} · {loan.assetCategory}</p>
                                        </div>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                        <p className="text-xs text-gray-500">Vay: <span className="font-semibold text-gray-700">{loan.loanAmount.toLocaleString('vi-VN')}đ</span></p>
                                        <p className="text-xs text-gray-500">Lãi: <span className="font-semibold text-gray-700">{loan.interestRate}%/tháng</span></p>
                                        <p className="text-xs text-gray-500">Bắt đầu: <span className="text-gray-700">{loan.startDate}</span></p>
                                        <p className="text-xs text-gray-500">Hạn trả: <span className={`font-semibold ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-600' : 'text-gray-700'}`}>{loan.dueDate}{isOverdue ? ` (quá ${Math.abs(daysLeft)} ngày)` : isDueSoon ? ` (${daysLeft} ngày)` : ''}</span></p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {pastLoans.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 mb-2">Đã đóng ({pastLoans.length})</p>
                              <div className="space-y-2">
                                {pastLoans.map(loan => {
                                  const cfg = statusConfigMap[loan.status];
                                  return (
                                    <div key={loan.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50/30 opacity-80">
                                      <div className="flex items-start justify-between gap-2 mb-1">
                                        <div>
                                          <p className="text-sm font-bold text-gray-700">{loan.id}</p>
                                          <p className="text-xs text-gray-500">{loan.assetName}</p>
                                        </div>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                        <p className="text-xs text-gray-500">Vay: <span className="font-semibold text-gray-600">{loan.loanAmount.toLocaleString('vi-VN')}đ</span></p>
                                        <p className="text-xs text-gray-500">Ngày đóng: <span className="text-gray-600">{loan.dueDate}</span></p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {customerLoans.length === 0 && (
                            <div className="border-2 border-dashed border-gray-200 rounded-xl py-6 text-center text-gray-400">
                              <span className="w-8 h-8 flex items-center justify-center mx-auto mb-1"><i className="ri-file-list-3-line text-2xl"></i></span>
                              <p className="text-xs">Chưa có khoản vay nào</p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <button
                    onClick={() => { setViewCustomer(null); setDeleteConfirm(viewCustomer.id); }}
                    className="w-full py-2.5 border border-red-200 text-red-500 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Xóa khách hàng này
                  </button>
                </>
              )}

              {/* ── CHẾ ĐỘ CHỈNH SỬA ── */}
              {editMode && editForm && (
                <>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Thông tin cá nhân</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Họ tên *', key: 'name', span: 2 },
                        { label: 'Số điện thoại *', key: 'phone', span: 1 },
                        { label: 'CMND/CCCD *', key: 'idCard', span: 1 },
                        { label: 'Địa chỉ', key: 'address', span: 2 },
                      ].map(f => (
                        <div key={f.key} className={f.span === 2 ? 'col-span-2' : ''}>
                          <label className="text-xs text-gray-500 font-medium block mb-1">{f.label}</label>
                          <input
                            value={(editForm as unknown as Record<string, unknown>)[f.key] as string || ''}
                            onChange={e => setEditForm(prev => prev ? { ...prev, [f.key]: e.target.value } : prev)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-id-card-line text-gray-400"></i></span>
                      Hình ảnh CCCD
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <CccdUpload
                        label="Mặt trước"
                        value={editForm.cccdFront}
                        inputRef={editFrontRef}
                        onClear={() => { setEditForm(p => p ? { ...p, cccdFront: '' } : p); if (editFrontRef.current) editFrontRef.current.value = ''; }}
                        onChange={e => handleImageUpload(e, 'cccdFront', 'edit')}
                      />
                      <CccdUpload
                        label="Mặt sau"
                        value={editForm.cccdBack}
                        inputRef={editBackRef}
                        onClear={() => { setEditForm(p => p ? { ...p, cccdBack: '' } : p); if (editBackRef.current) editBackRef.current.value = ''; }}
                        onChange={e => handleImageUpload(e, 'cccdBack', 'edit')}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => setEditMode(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="flex-1 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap"
                    >
                      Lưu thay đổi
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: THÊM KHÁCH HÀNG MỚI ===== */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-800">Thêm Khách Hàng Mới</h3>
              <button onClick={() => setShowAdd(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg"></i></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Thông tin cá nhân</p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Họ tên *', key: 'name', span: 2 },
                    { label: 'Số điện thoại *', key: 'phone', span: 1 },
                    { label: 'CMND/CCCD *', key: 'idCard', span: 1 },
                    { label: 'Địa chỉ', key: 'address', span: 2 },
                  ].map(f => (
                    <div key={f.key} className={f.span === 2 ? 'col-span-2' : ''}>
                      <label className="text-xs text-gray-500 font-medium block mb-1">{f.label}</label>
                      <input
                        value={(form as Record<string, unknown>)[f.key] as string}
                        onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-id-card-line text-gray-400"></i></span>
                  Hình ảnh CCCD (tùy chọn)
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <CccdUpload
                    label="Mặt trước"
                    value={form.cccdFront}
                    inputRef={cccdFrontRef}
                    onClear={() => { setForm(p => ({ ...p, cccdFront: '' })); if (cccdFrontRef.current) cccdFrontRef.current.value = ''; }}
                    onChange={e => handleImageUpload(e, 'cccdFront', 'add')}
                  />
                  <CccdUpload
                    label="Mặt sau"
                    value={form.cccdBack}
                    inputRef={cccdBackRef}
                    onClear={() => { setForm(p => ({ ...p, cccdBack: '' })); if (cccdBackRef.current) cccdBackRef.current.value = ''; }}
                    onChange={e => handleImageUpload(e, 'cccdBack', 'add')}
                  />
                </div>
                {(form.cccdFront || form.cccdBack) && (
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line"></i></span>
                    Ảnh CCCD sẽ được lưu vào hồ sơ khách hàng
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
                <button onClick={handleAdd} className="flex-1 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap">Lưu khách hàng</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">Xác nhận xóa?</h3>
            <p className="text-gray-500 text-sm text-center mb-2">Khách hàng sẽ bị xóa vĩnh viễn khỏi hệ thống.</p>
            <p className="text-red-400 text-xs text-center mb-5 font-medium">Tất cả khoản vay, tài sản tồn kho và doanh thu liên kết cũng sẽ bị xóa theo.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">Xóa</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

}
